import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Group, Skeleton, Stack, Text, px, useMantineTheme } from '@mantine/core';
import { Eventcalendar, MbscCalendarEvent } from '@mobiscroll/react';
import dayjs from 'dayjs';
import useAppointmentAgenda from 'hooks/appointment/useAppointmentAgenda';
import useClassAgenda from 'hooks/class/useClassAgenda';
import useDebounce from 'hooks/useDebounce';
import { AppointmentAgenda, KiloClassAgenda } from 'types';
import { getAppointmentEventTitle } from 'utils/event.helpers';
import { sortArray } from 'utils/functions';

import AgendaItem from './AgendaItem';
import AgendaEvent from './interface';
import useEventCalendarAgendaStyles from './styles';

interface FilterTerms {
  eventTypes: string[];
  coaches: string[];
  locations: string[];
}

interface IEventCalendarAgendaViewProps {
  eventCalendarNavigationButtons: () => JSX.Element;
  searchFilters: FilterTerms;
  createEventDate: Date;
  setCreateEventDate: React.Dispatch<Date>;
  calendarMaxDate?: Date;
  handleEditClassEvent: (kcId: string) => void;
  handleEditAppointmentEvent: (aId: string) => void;
  isDesktop: boolean;
}

const EventCalendarAgendaView = ({
  eventCalendarNavigationButtons,
  searchFilters,
  createEventDate,
  setCreateEventDate,
  calendarMaxDate,
  handleEditClassEvent,
  handleEditAppointmentEvent,
  isDesktop,
}: IEventCalendarAgendaViewProps): JSX.Element => {
  const theme = useMantineTheme();
  const { classes } = useEventCalendarAgendaStyles();
  const loadingContainerRef = useRef(document.createElement('div'));
  const [loaderRowNum, setLoaderRowNum] = useState(4);

  const debouncedCreateEventDate = useDebounce(dayjs(createEventDate).format('YYYY-MM-DD'), 200);
  const [calendarEvents, setCalendarEvents] = useState<MbscCalendarEvent[]>([]);

  const {
    data: classData,
    isError: classIsError,
    isLoading: classIsLoading,
    isSuccess: classIsSuccess,
  } = useClassAgenda({
    ...searchFilters,
    date: debouncedCreateEventDate,
  });

  const {
    data: appointmentData,
    isError: appointmentIsError,
    isLoading: appointmentIsLoading,
    isSuccess: appointmentIsSuccess,
  } = useAppointmentAgenda({
    ...searchFilters,
    date: debouncedCreateEventDate,
  });

  const onPageLoading = (event: {
    firstDay: string | number | Date | dayjs.Dayjs | null | undefined;
  }): void => {
    setCreateEventDate(dayjs(event.firstDay).startOf('day').toDate());
  };

  const handleEventClick = useCallback(
    (event: { original: AgendaEvent }): void => {
      if (event.original.eventType === 'class') {
        handleEditClassEvent(event.original.id);
      }
      if (event.original.eventType === 'appointment') {
        handleEditAppointmentEvent(event.original.id);
      }
    },
    [handleEditClassEvent, handleEditAppointmentEvent],
  );

  const renderAgenda = useCallback(
    (data: any): JSX.Element => {
      if (classIsLoading || appointmentIsLoading) {
        return (
          <Box ref={loadingContainerRef} py='md' sx={{ width: '100%', height: '100%' }}>
            {[...Array(loaderRowNum)].map((_, i) => (
              <Skeleton key={`skeleton-${i}`} height={px(theme.fontSizes.md) * 2} mb='xs' />
            ))}
          </Box>
        );
      }
      if (classIsSuccess && appointmentIsSuccess && data.length) {
        const events = data[0].events;
        const agendaItems = events.map((event: { original: AgendaEvent }) => {
          return (
            <AgendaItem
              key={event.original.id}
              event={event.original}
              onClick={() => handleEventClick(event)}
            />
          );
        });

        return (
          <Stack className={classes.agendaContent}>
            {isDesktop && (
              <Group className={`${classes.header} ${classes.grid}`}>
                {['Time', 'Event', 'Coach', 'Location', 'Event Size', 'Waitlist'].map(header => (
                  <Text c='dimmed' fs='italic' fw='normal' fz={'xs'} key={header}>
                    {header}
                  </Text>
                ))}
              </Group>
            )}
            {agendaItems}
          </Stack>
        );
      }
      return <></>;
    },
    [
      classIsLoading,
      appointmentIsLoading,
      classIsSuccess,
      appointmentIsSuccess,
      loaderRowNum,
      theme.fontSizes.md,
      classes.agendaContent,
      classes.header,
      classes.grid,
      isDesktop,
      handleEventClick,
    ],
  );

  useEffect(() => {
    if (appointmentIsSuccess && classIsSuccess && appointmentData && classData) {
      const events = [
        ...classData.data.map((event: KiloClassAgenda): MbscCalendarEvent => {
          const start = new Date(`${event.startDate}T${event.startTime}`);
          const end = new Date(`${event.startDate}T${event.startTime}`);
          end.setMinutes(start.getMinutes() + event.duration);
          return {
            id: event.kiloClassId,
            start,
            end,
            eventType: 'class',
            startTime: dayjs(start).format('h:mm A'),
            endTime: dayjs(end).format('h:mm A'),
            name: event.kiloClassType.name,
            location: event.location?.name,
            coaches: `${
              event.kiloClassCoachKiloClassCoachRoles.length
                ? `${event.kiloClassCoachKiloClassCoachRoles[0].coach.fullName} (${event.kiloClassCoachKiloClassCoachRoles[0].kiloClassCoachRole.name})`
                : ''
            }${
              event.kiloClassCoachKiloClassCoachRoles.length > 1
                ? ` + ${event.kiloClassCoachKiloClassCoachRoles.length - 1}`
                : ''
            }`,
            eventSize: `${
              event.athleteKiloClasses.filter(
                akc => (akc.isReserved || akc.isCheckedIn) && !akc.isLateCancel && !akc.isNoShow,
              ).length
            }/${event.capacity}`,
            waitlist: `${event.athleteKiloClasses.filter(akc => akc.isWaitlisted).length}/${
              event.waitlistLimit
            }`,
            color: event.kiloClassType.color,
          };
        }),

        ...appointmentData.data.map((event: AppointmentAgenda): MbscCalendarEvent => {
          const start = new Date(`${event.startDate}T${event.startTime}`);
          const end = new Date(`${event.startDate}T${event.startTime}`);
          end.setMinutes(start.getMinutes() + event.duration);
          return {
            id: event.appointmentId,
            start,
            end,
            eventType: 'appointment',
            startTime: dayjs(start).format('h:mm A'),
            endTime: dayjs(end).format('h:mm A'),
            name: getAppointmentEventTitle(event),
            location: event.location?.name,
            coaches: `${
              event.appointmentCoachAppointmentCoachRoles.length
                ? `${event.appointmentCoachAppointmentCoachRoles[0].coach.fullName} (${event.appointmentCoachAppointmentCoachRoles[0].appointmentCoachRole.name})`
                : ''
            }${
              event.appointmentCoachAppointmentCoachRoles.length > 1
                ? ` + ${event.appointmentCoachAppointmentCoachRoles.length - 1}`
                : ''
            }`,
            eventSize: `${
              event.athleteAppointments.filter(
                akc => (akc.isReserved || akc.isCheckedIn) && !akc.isLateCancel && !akc.isNoShow,
              ).length
            }`,
            waitlist: 'n/a',
            color: event.appointmentType.color,
          };
        }),
      ];
      setCalendarEvents(sortArray(events, { orderBy: 'start', reversed: false }));
    }
  }, [theme, classIsSuccess, classData, appointmentIsSuccess, appointmentData]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (loadingContainerRef.current?.clientHeight) {
      const heightOfContainer = loadingContainerRef.current.clientHeight - px(theme.fontSizes.md) * 2;
      setLoaderRowNum(Math.trunc(heightOfContainer / (px(theme.fontSizes.md) * 2 + px(theme.spacing.xs))));
    }
  });

  if (appointmentIsError || classIsError) {
    return <>Something went wrong</>;
  }
  return (
    <>
      <Eventcalendar
        renderHeader={eventCalendarNavigationButtons}
        view={{
          agenda: { type: 'day' },
        }}
        selectedDate={createEventDate}
        theme='ios'
        themeVariant='light'
        className={classes.calendar}
        data={calendarEvents}
        renderAgenda={renderAgenda}
        max={calendarMaxDate}
        onPageLoading={onPageLoading}
        width={isDesktop ? '100%' : '100vw'}
      />
    </>
  );
};

export default EventCalendarAgendaView;
